import { Highlight } from '@components/highlight/highlight';
import { useIsVenue } from '@hooks/use-is-venue';
import { useAppSelector } from '@redux/hooks';
import type { Decorated } from '@typings/vendor';
import { useAtomValue } from 'jotai';
import React, { useState } from 'react';
import { Link } from 'react-scroll';
import {
	detailedPricingStartingCostAtom,
	estimateUsuallySpendAtom,
} from '../../jotai/storefront/detailed-pricing';
import DetailsHelper from '../../pages/Storefront/containers/details/helpers';

import { EstimatedPricingDetailsModal } from '@components/shared-pricing/EstimatedPricingDetailsModal';
import { useEstimatedPricing } from '@hooks/use-estimated-pricing';
import { isExtendedPriceRangeSchemaExperimentEnabled } from '@pages/Storefront/utils/extendedPriceRangeSchemaExperiment';
import { Link as ODSLink } from '@tkww/orion-web-ds-action';
import {
	photographyServicesAtom,
	travelAvailabilityAtom,
	travelRangeAtom,
	videographyServicesAtom,
} from '../../jotai/storefront/organization';
import {
	formattedGuestCapacityAtom,
	startingCostAtom,
	venueEstimateUsuallySpendAtom,
} from '../../jotai/storefront/pricing';
import { availabilityDataAtom } from '../../pages/Storefront/components/AvailabilitySection/availability-atoms';
import Styles from './highlights.scss';

const CEREMONY_AREA_AMENITY_ID = 'd46b53a6-932e-4b77-8668-a2298c285a6d';
const RECEPTION_AREA_AMENITY_ID = '2ee9d5c0-4167-4450-9734-643c6bee601a';
const ZERO_TO_FIFTY_GUEST_CAPACITY_TEXT = '0 to 50 guests';

const EstimatedPrice = ({
	classes,
	estimatedPrice,
	shouldShow,
}: {
	classes: React.ComponentProps<typeof Highlight>['classes'];
	estimatedPrice: string | null | undefined;
	shouldShow: boolean;
}) => {
	const [isModalOpen, setIsModalOpen] = useState(false);

	return (
		<>
			{shouldShow && (
				<>
					<EstimatedPricingDetailsModal
						isModalOpen={isModalOpen}
						closeCallback={() => setIsModalOpen(false)}
					/>
					<Highlight
						iconName="dollar-circle"
						iconSize="md"
						classes={classes}
						highlightTitle={`${estimatedPrice} avg. price`}
						highlightText={
							<span>
								{'The Knot Estimate '}
								<ODSLink
									as="button"
									type="button"
									onClick={() => setIsModalOpen(true)}
									styleType="tertiary"
								>
									See details
								</ODSLink>
							</span>
						}
					/>
				</>
			)}
		</>
	);
};

const travelAreaText = (
	travelRange: string | null,
	travelAvailability: number | null,
) => {
	if (travelRange) return `Travel area: ${travelRange} `;
	if (travelAvailability) return `Travels up to ${travelAvailability} miles `;
	return null;
};

const TravelHighlightText = ({
	commonOffset,
}: {
	commonOffset: number;
}) => {
	const travelAvailability = useAtomValue(travelAvailabilityAtom);
	const travelRange = useAtomValue(travelRangeAtom);
	const travelArea = travelAreaText(travelRange, travelAvailability);
	if (!travelArea) {
		return <>Service area</>;
	}

	return (
		<span>
			{travelArea}
			<Link
				href={'#location'}
				to="location"
				smooth
				spy
				isDynamic
				offset={commonOffset}
			>
				See details
			</Link>
		</span>
	);
};

export const Highlights = () => {
	const vendor = useAppSelector((state) => state.vendor.vendor as Decorated);
	const isVenue = useIsVenue();
	const availabilityData = useAtomValue(availabilityDataAtom);

	const serviceAreas = vendor.serviceArea;
	const videographyServices = useAtomValue(videographyServicesAtom);
	const photographyServices = useAtomValue(photographyServicesAtom);
	const guestCapacity = useAtomValue(formattedGuestCapacityAtom);
	const guestCapacityHighlightText =
		guestCapacity === ZERO_TO_FIFTY_GUEST_CAPACITY_TEXT
			? 'Actual capacity may vary'
			: null;

	const startingCost = useAtomValue(
		isVenue ? startingCostAtom : detailedPricingStartingCostAtom,
	);

	const hasCeremonyAndReceptionArea =
		vendor.amenities.filter((amenity: Settings.VenueAmenity) => {
			if (
				amenity.id === CEREMONY_AREA_AMENITY_ID ||
				amenity.id === RECEPTION_AREA_AMENITY_ID
			) {
				return amenity;
			}
		}).length === 2;

	const currentYear = new Date().getFullYear();

	const highlightClasses = {
		title: Styles.highlightsTitle,
		text: Styles.highlightsText,
		container: Styles.highlightsContainer,
		subContainer: Styles.highlightsTextContainer,
	};

	const hasDetails = DetailsHelper.hasDetails(vendor);
	const isInEstimatedPricing = useEstimatedPricing();
	const estimatedPrice = useAtomValue(venueEstimateUsuallySpendAtom);
	const estimatedPriceNonVenue = useAtomValue(estimateUsuallySpendAtom);
	const shouldShowVenueEstimatedPricing = Boolean(
		isInEstimatedPricing && isVenue && !startingCost && estimatedPrice,
	);
	const shouldShowNonVenueEstimatedPricing = Boolean(
		isInEstimatedPricing && !isVenue && !startingCost && estimatedPriceNonVenue,
	);
	const isPriceRangeDisplayEnabled =
		startingCost &&
		!isExtendedPriceRangeSchemaExperimentEnabled(vendor.displayId);

	const hasHighlights =
		startingCost ||
		serviceAreas ||
		guestCapacity ||
		hasCeremonyAndReceptionArea ||
		videographyServices ||
		photographyServices ||
		availabilityData ||
		shouldShowVenueEstimatedPricing ||
		shouldShowNonVenueEstimatedPricing;

	const commonOffset = -25;

	if (hasHighlights) {
		return (
			<div className={Styles.container}>
				{isPriceRangeDisplayEnabled && (
					<meta
						itemProp="priceRange"
						content={`${startingCost}+`}
						data-testid="meta-price-range"
					/>
				)}
				<EstimatedPrice
					classes={highlightClasses}
					estimatedPrice={estimatedPrice}
					shouldShow={shouldShowVenueEstimatedPricing}
				/>
				<EstimatedPrice
					classes={highlightClasses}
					estimatedPrice={estimatedPriceNonVenue}
					shouldShow={shouldShowNonVenueEstimatedPricing}
				/>
				{startingCost && (
					<Highlight
						iconName="dollar-circle"
						iconSize="md"
						classes={highlightClasses}
						highlightTitle={`${startingCost} starting price`}
						highlightText={
							<span>
								<Link
									href={'#navPricing'}
									to="navPricing"
									smooth
									spy
									isDynamic
									offset={0}
								>
									See details
								</Link>
							</span>
						}
					/>
				)}
				{serviceAreas && (
					<Highlight
						iconName="pin"
						iconSize="md"
						classes={highlightClasses}
						highlightTitle={serviceAreas}
						highlightText={<TravelHighlightText commonOffset={commonOffset} />}
					/>
				)}
				{guestCapacity && (
					<Highlight
						iconName="couple"
						iconSize="md"
						classes={highlightClasses}
						highlightTitle={guestCapacity}
						highlightText={guestCapacityHighlightText}
					/>
				)}

				{hasCeremonyAndReceptionArea && (
					<Highlight
						iconName="rounded-altar"
						iconSize="md"
						classes={highlightClasses}
						highlightTitle="Holds ceremonies and receptions"
					/>
				)}
				{videographyServices && (
					<Highlight
						iconName="camera-video"
						iconSize="md"
						classes={highlightClasses}
						highlightTitle="Videography services available"
						highlightText={
							hasDetails && (
								<Link
									href={'#navDetails'}
									to="navDetails"
									smooth
									spy
									isDynamic
									offset={6}
								>
									See services
								</Link>
							)
						}
					/>
				)}
				{photographyServices && (
					<Highlight
						iconName="camera-pro"
						iconSize="md"
						classes={highlightClasses}
						highlightTitle="Photography services available"
						highlightText={
							hasDetails && (
								<Link
									href={'#navDetails'}
									to="navDetails"
									smooth
									spy
									isDynamic
									offset={commonOffset}
								>
									See services
								</Link>
							)
						}
					/>
				)}
				{availabilityData && (
					<Highlight
						iconName="calendar"
						iconSize="md"
						classes={highlightClasses}
						highlightTitle={`Booking in ${currentYear}, ${currentYear + 1}`}
						highlightText={
							<Link
								href={'#navAvailability'}
								to="navAvailability"
								smooth
								spy
								isDynamic
								offset={0}
							>
								See available dates
							</Link>
						}
					/>
				)}
			</div>
		);
	}

	return null;
};
